import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import moment from 'moment';
import { SearchOperatorsService } from '../../Search/searchOperators.service';
import { normalizeActionViewRequest } from './actionView.adapters';
import { ActionViewApiService } from './actionView.service';
import {
    ActionViewPickerItem,
    ActionViewSearchRequest,
    TaskActionViewCompletedOption,
    TaskActionViewDateTimeOption,
    TaskActionViewIncludeOption,
    TaskActionViewShowOption,
    TaskActionViewThatAreOption,
    TaskActionViewWhereOption,
} from './actionView.models';

interface PlainEnglishFilterColumn {
    columnId?: number;
    resultColumnName?: string;
    resultsColumnName?: string;
    or?: Array<{
        operator: string | { name: string; displayName?: string };
        value: unknown;
    }>;
}

@Injectable({ providedIn: 'root' })
export class ActionViewPlainEnglishService {
    constructor(
        private readonly actionViewService: ActionViewApiService,
        private readonly searchOperators: SearchOperatorsService,
        private readonly sanitizer: DomSanitizer,
    ) {}

    getForFilter(filter: unknown, filterColumns: PlainEnglishFilterColumn[]): string {
        const request = this.toRequest(filter);
        let text = !request.taskTypeName || request.taskTypeName === 'All' ? 'All ' : '';
        text += this.getShowText(request);
        text +=
            request.taskTypeName && request.taskTypeName !== 'All' ? ` <strong>${request.taskTypeName}</strong>` : '';
        text += ` ${this.getWhereText(request)}`;
        text += ` ${this.getThatAreText(request)}`;

        if (request.thatAreChoice === TaskActionViewThatAreOption.Completed) {
            text += this.getIncludeText(request);
            text += this.getCompletedDateText(request);
        }

        text += this.getDateText(request);
        text += this.getUrgencyText(request);

        if (request.includeUnscheduled) {
            text += request.onlyUnscheduled ? ' AND only' : ' OR';
            text += ' Unscheduled';
        }

        if (request.useLastActivityFilter && (request.lastActivityTo !== null || request.lastActivityFrom !== null)) {
            text += this.getLastActivityText(request);
        }

        const populatedColumns = filterColumns.filter((column) =>
            (column.or ?? []).some((element) => {
                const operator = typeof element.operator === 'string' ? element.operator : element.operator?.name;
                return Boolean(element.value) || ['blank', 'notBlank', 'isNA'].includes(operator ?? '');
            }),
        );
        const filters = request.filters.length ? request.filters : populatedColumns;
        if (filters.length) {
            text += this.getFilterText(filters as PlainEnglishFilterColumn[]);
        }

        return this.sanitizer.sanitize(SecurityContext.HTML, text) ?? '';
    }

    private toRequest(filter: unknown): ActionViewSearchRequest {
        if (typeof filter === 'object' && filter !== null && 'showChoice' in filter) {
            return filter as ActionViewSearchRequest;
        }
        return normalizeActionViewRequest(filter);
    }

    private getShowText(request: ActionViewSearchRequest): string {
        switch (request.showChoice) {
            case TaskActionViewShowOption.MyTasks:
                return 'My';
            case TaskActionViewShowOption.MyTeamsTasks:
                return "My team's";
            case TaskActionViewShowOption.SpecificIndividualsTasks:
                return this.getUserText(request.showUserTeams, "Specific Individual's", true);
            case TaskActionViewShowOption.SpecificTeamsTasks:
                return this.getTeamText(request.showTeams, "Specific team's", true);
            default:
                return '';
        }
    }

    private getWhereText(request: ActionViewSearchRequest): string {
        switch (request.whereChoice) {
            case TaskActionViewWhereOption.Assigned:
                return 'assigned tasks';
            case TaskActionViewWhereOption.UltimatelyResponsible:
                return 'tasks where ultimately responsible';
            case TaskActionViewWhereOption.AssignedOrUltimatelyResponsible:
                return 'assigned tasks or tasks where ultimately responsible';
            case TaskActionViewWhereOption.AccountHandler:
                return 'tasks for companies where account handler';
            case TaskActionViewWhereOption.ComplianceReturnsPPResp:
                return 'tasks for companies where Compliance Returns PP Resp';
            default:
                return 'Tasks';
        }
    }

    private getThatAreText(request: ActionViewSearchRequest): string {
        switch (request.thatAreChoice) {
            case TaskActionViewThatAreOption.Ready:
                return 'that are READY';
            case TaskActionViewThatAreOption.NotReady:
                return 'that are NOT READY';
            case TaskActionViewThatAreOption.ReadyOrNotReady:
                return 'that are READY or NOT READY';
            default:
                return '';
        }
    }

    private getIncludeText(request: ActionViewSearchRequest): string {
        let prefix = request.includeSkipped && request.onlySkipped ? 'skipped' : 'completed';
        if (prefix === 'completed' && request.includeSkipped) {
            prefix += ' OR skipped';
        }
        switch (request.includeChoice) {
            case TaskActionViewIncludeOption.CompletedByMe:
                return `${prefix} by me`;
            case TaskActionViewIncludeOption.CompletedByMyTeam:
                return `${prefix} by my team`;
            case TaskActionViewIncludeOption.CompletedBySpecificIndividual:
                return `${prefix} by ${this.getUserText(request.includeUserTeams, 'a Specific Individual', false)}`;
            case TaskActionViewIncludeOption.CompletedBySpecificTeam:
                return `${prefix} by ${this.getTeamText(request.includeTeams, 'a Specific Team', false)}`;
            default:
                return prefix;
        }
    }

    private getCompletedDateText(request: ActionViewSearchRequest): string {
        if (request.completedChoice === TaskActionViewCompletedOption.CompletedWithinLastDays) {
            return request.completedWithinDays ? ` within the last ${request.completedWithinDays} days` : '';
        }
        let text = request.completedDateFrom
            ? ` on or after ${moment.utc(request.completedDateFrom).format('M/D/YYYY')} `
            : '';
        if (request.completedDateTo) {
            text += request.completedDateFrom ? ' and' : '';
            text += ` by ${moment.utc(request.completedDateTo).format('M/D/YYYY')}`;
        }
        return text;
    }

    private getDateText(request: ActionViewSearchRequest): string {
        if (request.onlyUnscheduled) {
            return '';
        }
        if (request.dateTimeChoice === TaskActionViewDateTimeOption.DueWithinDays) {
            return request.dueWithinDays ? ` and due within the next ${request.dueWithinDays} days` : '';
        }
        if (request.dateTimeChoice !== TaskActionViewDateTimeOption.DueDateRange) {
            return '';
        }
        let text = request.dueDateFrom
            ? ` and due on or after ${moment.utc(request.dueDateFrom).format('M/D/YYYY')} `
            : '';
        if (request.dueDateTo) {
            text += ` and due by ${moment.utc(request.dueDateTo).format('M/D/YYYY')}`;
        }
        return text;
    }

    private getUrgencyText(request: ActionViewSearchRequest): string {
        if (!request.useUrgencyFilter) {
            return '';
        }
        const urgency = this.actionViewService.getUrgencies().find((item) => item.id === request.urgencyChoice);
        return urgency ? ` with an urgency of ${urgency.display}` : '';
    }

    private getLastActivityText(request: ActionViewSearchRequest): string {
        let value: string;
        if (request.lastActivityTo === 0 && request.lastActivityFrom === 0) {
            value = 'today';
        } else if (request.lastActivityTo === null) {
            value = `greater than ${request.lastActivityFrom}`;
        } else if (request.lastActivityFrom === null) {
            value = `up to ${request.lastActivityTo}`;
        } else if (request.lastActivityTo === request.lastActivityFrom) {
            value = String(request.lastActivityTo);
        } else if (request.lastActivityTo === 9999) {
            value = `${request.lastActivityFrom}+`;
        } else {
            value = `${request.lastActivityFrom} to ${request.lastActivityTo}`;
        }
        return `, and last activity is ${value}${value === 'today' ? '' : ' days ago'}`;
    }

    private getUserText(items: ActionViewPickerItem[], emptyText: string, possessive: boolean): string {
        const names = items.filter((item) => Boolean(item.userID)).map((item) => this.getUserName(item));
        return names.length ? `${this.joinNames(names)}${possessive ? "'s" : ''}` : emptyText;
    }

    private getTeamText(items: ActionViewPickerItem[], emptyText: string, possessive: boolean): string {
        const names = items
            .filter((item) => item.teamID !== null && item.teamID !== undefined)
            .map((item) => item.team?.name ?? item.name ?? '')
            .filter(Boolean);
        return names.length ? `${this.joinNames(names)}${possessive ? "'s" : ''}` : emptyText;
    }

    private getUserName(item: ActionViewPickerItem): string {
        if (item.user?.firstName || item.user?.lastName) {
            return `${item.user.firstName ?? ''} ${item.user.lastName ?? ''}`.trim();
        }
        return item.name ?? '';
    }

    private joinNames(names: string[]): string {
        if (names.length < 2) {
            return names[0] ?? '';
        }
        return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    }

    private getFilterText(filters: PlainEnglishFilterColumn[]): string {
        const operatorMap = this.searchOperators.displayNameMap();
        const columns = filters.map((column) => {
            const expressions = (column.or ?? []).map((element) => {
                const operatorName = typeof element.operator === 'string' ? element.operator : element.operator.name;
                const operator =
                    typeof element.operator === 'string'
                        ? operatorMap[operatorName]
                        : { ...operatorMap[operatorName], ...element.operator };
                let text = operator?.displayName?.toLowerCase() ?? operatorName;
                if (!['blank', 'notBlank', 'nullable', 'isNA'].includes(operatorName)) {
                    const value = moment.isDate(element.value)
                        ? moment.utc(element.value).format('M/D/YYYY')
                        : String(element.value ?? '');
                    text += ` <em>${value}${operatorName === 'withinDate' ? ' days' : ''}</em>`;
                }
                return text;
            });
            return `${column.resultColumnName ?? column.resultsColumnName ?? ''} ${expressions.join(' OR ')}`;
        });
        return `; further limited by ${columns.join(' AND ')}`;
    }
}
